import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Box, Paper, Grid, Typography } from '@mui/material';
import { fK, fX, fV, fDate } from '../utils/formatters';
import { aggregateAds } from '../utils/scopeAggregator';
const ProjectionsTab = ({ report }) => {
    const [scope, setScope] = useState('all');
    const ads = report.ads || [];
    // Derive fail/opt counts + sums that the standalone tool recomputes
    // on the fly. Our backend already provides some; we use both.
    const allFa = useMemo(() => ads.filter((a) => a.result === 'Fail'), [ads]);
    const allOa = useMemo(() => ads.filter((a) => a.result === 'Optimize'), [ads]);
    const allPa = useMemo(() => ads.filter((a) => a.result === 'Pass'), [ads]);
    // Period-bound aggregate (only used when scope === 'period')
    const periodAgg = useMemo(() => aggregateAds(ads, 'period', report.period_start, report.period_end), [ads, report.period_start, report.period_end]);
    const periodAds = useMemo(() => {
        if (!report.period_start || !report.period_end)
            return ads;
        const ps = String(report.period_start).slice(0, 10);
        const pe = String(report.period_end).slice(0, 10);
        return ads.filter((a) => a.minDate && a.minDate >= ps && a.minDate <= pe);
    }, [ads, report.period_start, report.period_end]);
    /**
     * Build the active projections object based on scope. When scope === 'all'
     * we use the saved server-computed projections (no logic change). When
     * scope === 'period' we recompute using the SAME formulas as the backend
     * computeProjections() function — only the input set is filtered.
     */
    const { p, fa, oa, pa } = useMemo(() => {
        if (scope === 'all') {
            return { p: report.projections, fa: allFa, oa: allOa, pa: allPa };
        }
        // Period-bound recompute — mirrors backend computeProjections() formulas exactly
        const fa_p = periodAds.filter((a) => a.result === 'Fail');
        const oa_p = periodAds.filter((a) => a.result === 'Optimize');
        const pa_p = periodAds.filter((a) => a.result === 'Pass');
        const totalSpend = periodAgg.totalSpend;
        const totalRevenue = periodAgg.totalRevenue;
        const actualROAS = periodAgg.actualROAS;
        const avgPassROAS = periodAgg.avgPassROAS;
        const passSpend = periodAgg.passSpend;
        const passRevenue = periodAgg.passRevenue;
        const failWastedSpend = periodAgg.failWastedSpend;
        const failWastedRev = periodAgg.failWastedRev;
        const optWastedSpend = periodAgg.optWastedSpend;
        const optWastedRev = periodAgg.optWastedRev;
        const failOptWastedSpend = periodAgg.failOptWastedSpend;
        const failOptWastedRev = periodAgg.failOptWastedRev;
        // Conservative (Fail only paused)
        const consAddRev = failWastedSpend * avgPassROAS;
        const consNetRev = totalRevenue - failWastedRev + consAddRev;
        const consROAS = totalSpend > 0 ? consNetRev / totalSpend : 0;
        // Balanced (Fail fully + Optimize 50%)
        const optWastedSpend50 = optWastedSpend * 0.5;
        const optWastedRev50 = optWastedRev * 0.5;
        const balRealloc = failWastedSpend + optWastedSpend50;
        const balAddRev = balRealloc * avgPassROAS;
        const balNetRev = totalRevenue - failWastedRev - optWastedRev50 + balAddRev;
        const balROAS = totalSpend > 0 ? balNetRev / totalSpend : 0;
        // Full Potential
        const fullAddRev = failOptWastedSpend * avgPassROAS;
        const fullNetRev = totalRevenue - failOptWastedRev + fullAddRev;
        const fullROAS = totalSpend > 0 ? fullNetRev / totalSpend : 0;
        return {
            p: {
                totalSpend, totalRevenue, actualROAS, avgPassROAS,
                passSpend, passRevenue,
                failWastedSpend, failWastedRev,
                optWastedSpend, optWastedSpend50, optWastedRev50,
                failOptWastedSpend, failOptWastedRev,
                consAddRev, consNetRev, consROAS,
                balRealloc, balAddRev, balNetRev, balROAS,
                fullAddRev, fullNetRev, fullROAS,
            },
            fa: fa_p, oa: oa_p, pa: pa_p,
        };
    }, [scope, report.projections, allFa, allOa, allPa, periodAds, periodAgg]);
    const fws = p.failWastedSpend;
    const fwr = p.failWastedRev;
    const fows = p.failOptWastedSpend;
    const fowr = p.failOptWastedRev;
    // Evaluation spend — sum of atSpend across fail/opt
    const feS = fa.reduce((s, a) => s + (a.atSpend || 0), 0);
    const oeS = oa.reduce((s, a) => s + (a.atSpend || 0), 0);
    const cA = p.consAddRev; // additional revenue from conservative reallocation
    const cN = p.consNetRev;
    const bA = p.balAddRev;
    const bN = p.balNetRev;
    const fA = p.fullAddRev;
    const fN = p.fullNetRev;
    // 50% opt-wasted derivable from full-opt vs fail
    const ow50 = Math.max(0, fows - fws) * 0.5;
    const owr50 = p.optWastedRev50 ?? Math.max(0, fowr - fwr) * 0.5;
    const maxRev = Math.max(p.totalRevenue || 0, cN || 0, bN || 0, fN || 0);
    const scenarios = useMemo(() => [
        {
            key: 'actual',
            label: 'Actual',
            color: '#8C1B39',
            borderColor: '#8C1B39',
            desc: 'Real performance as spent',
            spend: p.totalSpend,
            revenue: p.totalRevenue,
            roas: p.actualROAS,
            addRev: null,
            addSub: undefined,
            roasGain: null,
        },
        {
            key: 'conservative',
            label: 'Conservative',
            color: '#711322',
            borderColor: '#711322',
            desc: `Fail ads paused · ${fK(fws)} reallocated`,
            spend: p.totalSpend,
            revenue: cN,
            roas: p.consROAS,
            addRev: cN - p.totalRevenue,
            addSub: `Gain: ${fK(cA)} − Lost: ${fK(fwr)}`,
            roasGain: p.actualROAS > 0
                ? ((p.consROAS - p.actualROAS) / p.actualROAS) * 100
                : null,
        },
        {
            key: 'balanced',
            label: 'Balanced',
            color: '#002B5B',
            borderColor: '#002B5B',
            desc: `Fail + 50% Opt · ${fK(fws + ow50)} reallocated`,
            spend: p.totalSpend,
            revenue: bN,
            roas: p.balROAS,
            addRev: bN - p.totalRevenue,
            addSub: `Gain: ${fK(bA)} − Lost: ${fK(fwr + owr50)}`,
            roasGain: p.actualROAS > 0
                ? ((p.balROAS - p.actualROAS) / p.actualROAS) * 100
                : null,
        },
        {
            key: 'full',
            label: 'Full Potential',
            color: '#003B30',
            borderColor: '#003B30',
            desc: `Fail+Optimize paused · ${fK(fows)} reallocated`,
            spend: p.totalSpend,
            revenue: fN,
            roas: p.fullROAS,
            addRev: fN - p.totalRevenue,
            addSub: `Gain: ${fK(fA)} − Lost: ${fK(fowr)}`,
            roasGain: p.actualROAS > 0
                ? ((p.fullROAS - p.actualROAS) / p.actualROAS) * 100
                : null,
        },
    ], [p, fws, fwr, fows, fowr, ow50, owr50, cA, bA, fA, cN, bN, fN]);
    return (_jsxs(Box, { sx: { p: 3 }, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                }, children: [_jsx(Box, { sx: {
                            display: 'inline-flex',
                            gap: 0.5,
                            p: 0.5,
                            bgcolor: '#F2F4F7',
                            borderRadius: '8px',
                        }, children: ['all', 'period'].map((s) => (_jsx(Box, { onClick: () => setScope(s), sx: {
                                px: 2,
                                py: 0.75,
                                fontSize: 12,
                                fontWeight: scope === s ? 700 : 500,
                                color: scope === s ? '#101828' : '#475467',
                                bgcolor: scope === s ? '#fff' : 'transparent',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                boxShadow: scope === s ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                transition: 'all 0.15s',
                            }, children: s === 'all' ? 'All-time / Lifetime' : 'Report Period' }, s))) }), scope === 'period' && (_jsxs(Typography, { sx: { fontSize: 11, color: '#667085' }, children: ["Showing ", periodAgg.ads_in_scope, " of ", ads.length, " ads (minDate within", ' ', fDate(report.period_start), " \u2013 ", fDate(report.period_end), ")"] }))] }), _jsxs(Paper, { elevation: 0, sx: {
                    p: 2.5,
                    mb: 2.75,
                    border: '1px solid #EAECF0',
                    borderLeft: '4px solid #EE2A7B',
                    borderRadius: '8px',
                    bgcolor: '#fff',
                }, children: [_jsx(Typography, { sx: {
                            fontSize: 10,
                            color: '#EE2A7B',
                            letterSpacing: '0.12em',
                            fontWeight: 700,
                            mb: 0.5,
                        }, children: "G8A METHODOLOGY" }), _jsx(Typography, { sx: { fontSize: 20, fontWeight: 800, color: '#101828', mb: 0.5 }, children: "Projections" }), _jsxs(Typography, { sx: { fontSize: 12, color: '#667085' }, children: ["Underperforming ads paused at $", report.threshold, " \u00B7 budget reallocated to Pass ads \u00B7 total spend unchanged"] })] }), _jsxs(Grid, { container: true, spacing: 1.5, sx: { mb: 3 }, children: [_jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(KpiCard, { label: "Avg Pass ROAS", value: fX(p.avgPassROAS), sub: `${pa.length} pass ads · ${fK(p.passSpend)}`, color: "#003B30" }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(KpiCard, { label: "Wasted Spend (Failed Ads)", value: fK(fws), sub: `${fa.length} ads · eval: ${fK(feS)}`, color: fws <= fwr ? '#003B30' : '#8C1B39' }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(KpiCard, { label: "Revenue from Failed Ads", value: fK(fwr), sub: "Revenue post-threshold", color: fws <= fwr ? '#003B30' : '#8C1B39' }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(KpiCard, { label: "Wasted Spend (Fail + Optimized Ads)", value: fK(fows), sub: `${fa.length + oa.length} ads · eval: ${fK(feS + oeS)}`, color: "#711322" }) })] }), _jsxs(Paper, { elevation: 0, sx: {
                    bgcolor: '#FAFAFA',
                    border: '1px solid #EAECF0',
                    borderRadius: '8px',
                    p: 2.25,
                    mb: 2.75,
                }, children: [_jsx(Typography, { sx: { fontSize: 12, fontWeight: 700, color: '#101828', mb: 2 }, children: "Projected Revenue Comparison" }), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.25 }, children: scenarios.map((sc) => {
                            const pct = maxRev > 0 ? (sc.revenue / maxRev) * 100 : 0;
                            const isActual = sc.key === 'actual';
                            return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5 }, children: [_jsx(Typography, { sx: {
                                            width: 95,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: sc.color,
                                            flexShrink: 0,
                                        }, children: sc.label }), _jsx(Box, { sx: {
                                            flex: 1,
                                            bgcolor: '#E8E8EE',
                                            borderRadius: '6px',
                                            height: 22,
                                            overflow: 'hidden',
                                        }, children: _jsx(Box, { sx: {
                                                width: `${pct}%`,
                                                height: '100%',
                                                bgcolor: isActual ? '#E8E8EE' : sc.color,
                                                borderRadius: '6px',
                                                border: isActual ? `1.5px dashed ${sc.color}` : 'none',
                                                backgroundImage: isActual
                                                    ? `repeating-linear-gradient(45deg, ${sc.color}22 0px, ${sc.color}22 3px, transparent 3px, transparent 8px)`
                                                    : 'none',
                                                transition: 'width .6s ease',
                                            } }) }), _jsx(Typography, { sx: {
                                            width: 95,
                                            fontSize: 12,
                                            fontWeight: 800,
                                            color: sc.color,
                                            textAlign: 'right',
                                            flexShrink: 0,
                                        }, children: fK(sc.revenue) }), _jsx(Box, { sx: { width: 50, flexShrink: 0 }, children: !isActual && sc.roasGain != null && (_jsx(Typography, { sx: {
                                                fontSize: 10,
                                                fontWeight: 700,
                                                textAlign: 'right',
                                                color: sc.roasGain >= 0 ? '#003B30' : '#8C1B39',
                                            }, children: fV(sc.roasGain) })) })] }, sc.key));
                        }) }), _jsx(Typography, { sx: { mt: 1.25, fontSize: 10, color: '#667085' }, children: "% column = ROAS improvement vs Actual" })] }), _jsx(Grid, { container: true, spacing: 1.5, children: scenarios.map((sc) => (_jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsxs(Paper, { elevation: 0, sx: {
                            border: `2px solid ${sc.borderColor}`,
                            borderRadius: '14px',
                            p: 2.25,
                            bgcolor: sc.key !== 'actual' ? `${sc.color}04` : '#fff',
                            height: '100%',
                        }, children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 0.75,
                                }, children: [_jsx(Typography, { sx: { fontWeight: 800, color: sc.color, fontSize: 13 }, children: sc.label }), sc.key !== 'actual' && sc.roasGain != null && (_jsx(Box, { sx: {
                                            bgcolor: sc.roasGain >= 0 ? '#E0EEEB' : '#F5E0E5',
                                            color: sc.roasGain >= 0 ? '#003B30' : '#8C1B39',
                                            fontSize: 10,
                                            fontWeight: 800,
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: '8px',
                                        }, children: fV(sc.roasGain) }))] }), _jsx(Typography, { sx: {
                                    fontSize: 10,
                                    color: '#667085',
                                    mb: 1.75,
                                    lineHeight: 1.4,
                                    minHeight: 28,
                                }, children: sc.desc }), _jsx(SRRow, { label: "Total Spend", value: fK(sc.spend), large: true }), _jsx(SRRow, { label: sc.key === 'actual'
                                    ? 'Total Revenue'
                                    : 'Projected Revenue', value: fK(sc.revenue), color: sc.key !== 'actual' ? sc.color : undefined, large: true }), _jsx(SRRow, { label: sc.key === 'actual' ? 'Blended ROAS' : 'Projected ROAS', value: fX(sc.roas), color: sc.key !== 'actual' ? sc.color : undefined, large: true }), _jsx(Box, { sx: { borderTop: '1px solid #EAECF0', my: 1.5 } }), sc.key === 'actual' ? (_jsxs(_Fragment, { children: [_jsx(SRRow, { label: "Additional Revenue", value: "baseline", color: "#98A2B3" }), _jsx(SRRow, { label: "ROAS Improvement", value: "\u2014", color: "#98A2B3" })] })) : (_jsxs(_Fragment, { children: [_jsx(SRRow, { label: "Additional Revenue", value: fK(sc.addRev), color: (sc.addRev ?? 0) >= 0 ? '#003B30' : '#8C1B39', sub: sc.addSub, large: true }), _jsx(SRRow, { label: "ROAS Improvement", value: fV(sc.roasGain), color: (sc.roasGain ?? 0) >= 0 ? '#003B30' : '#8C1B39' })] }))] }) }, sc.key))) }), _jsxs(Box, { sx: {
                    mt: 2.25,
                    px: 2,
                    py: 1.5,
                    bgcolor: '#F5F5F7',
                    borderRadius: '8px',
                    fontSize: 10,
                    color: '#667085',
                    lineHeight: 1.8,
                }, children: [_jsx("strong", { style: { color: '#475467' }, children: "Methodology:" }), " Evaluation spend (first $", report.threshold, " per ad) is never wasted. Additional Revenue =", ' ', "(Wasted spend \u00D7 Avg Pass ROAS) \u2212 Revenue lost from pausing. Total spend is unchanged in all scenarios. Avg Pass ROAS = ", fX(p.avgPassROAS), "."] })] }));
};
// ── Helpers ──────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, color }) => (_jsxs(Paper, { elevation: 0, sx: {
        p: 2.25,
        border: '1px solid #EAECF0',
        borderLeft: `4px solid ${color}`,
        borderRadius: '8px',
        bgcolor: '#fff',
        height: '100%',
    }, children: [_jsx(Typography, { sx: {
                fontSize: 9,
                color: '#98A2B3',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                mb: 1,
            }, children: label }), _jsx(Typography, { sx: {
                fontSize: 24,
                fontWeight: 800,
                color,
                letterSpacing: '-0.02em',
                lineHeight: 1,
            }, children: value }), sub && (_jsx(Typography, { sx: { fontSize: 10, color: '#98A2B3', mt: 0.75 }, children: sub }))] }));
const SRRow = ({ label, value, sub, color, large }) => (_jsxs(Box, { sx: { mb: 1.25 }, children: [_jsx(Typography, { sx: { fontSize: 10, color: '#98A2B3', fontWeight: 600, mb: 0.25 }, children: label }), _jsx(Typography, { sx: {
                fontSize: large ? 18 : 13,
                fontWeight: 800,
                color: color || '#101828',
                lineHeight: 1.2,
            }, children: value }), sub && (_jsx(Typography, { sx: { fontSize: 9, color: '#98A2B3', mt: 0.25 }, children: sub }))] }));
export default ProjectionsTab;
